import { FileText, Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import Loader from "../../../components/Loader";
import { Button } from "../../../components/ui/button";
import { buildDocumentUrl } from "../../../handlers/documentStorage";
import { useSmartProfile } from "../../../services/create-resume/queries";
import { Modal } from "../../Profile/components/Modal";
import UploadResumeModal from "../../Profile/components/UploadResumeModal";

interface DSP {
	dsp_id: string;
	dsp_title: string;
	dsp_description: string;
	// The API writes the DSP row before the file reaches S3, so a failed upload leaves a DSP behind with nothing to compare against.
	source_resume?: {
		doc_id: string;
		file_name: string;
		storage_key: string;
	};
}

interface SelectDSPProps {
	runFitCheck: (resumeURL: string) => void;
}

function SelectDSP({ runFitCheck }: SelectDSPProps) {
	const { data: smartProfileData, isLoading, refetch: refetchSmartProfile } = useSmartProfile();
	// Track the DSP, not its storage key: the card highlight is keyed off dsp_id, so storing the
	// key here left every card looking unselected even once one had been picked.
	const [selectedDSPId, setSelectedDSPId] = useState<string | null>(null);
	const [showCreateDSP, setShowCreateDSP] = useState(false);
	// The dsp_ids that existed before the create modal opened, or null when we are not waiting on
	// a new one. Whatever appears that is not in here is the DSP the user just made. POST
	// smart-profiles is not known to return the new dsp_id (see findDspIdByFileHash), so diffing
	// the list is the one identification that does not depend on the response shape.
	const dspIdsBeforeCreateRef = useRef<string[] | null>(null);

	const dsps: DSP[] = smartProfileData?.dsps || [];

	const selectedStorageKey = dsps.find((dsp) => dsp.dsp_id === selectedDSPId)?.source_resume?.storage_key;

	const handleRunFitCheck = () => {
		if (!selectedStorageKey) return;

		runFitCheck(buildDocumentUrl(selectedStorageKey));
	};

	const openCreateDSP = () => {
		dspIdsBeforeCreateRef.current = dsps.map((dsp) => dsp.dsp_id);
		setShowCreateDSP(true);
	};

	const closeCreateDSP = () => {
		setShowCreateDSP(false);
		// The create mutation invalidates this query when the row is written, which is before the
		// file reaches S3 — so the cached copy can still be missing source_resume. Refetch once the
		// upload has finished to get the DSP in its runnable state.
		refetchSmartProfile();
	};

	// Select the new DSP for the user: they created it to run this FitCheck, so making them pick it
	// out of the list again is a step for nothing. Waits for source_resume, which the API attaches
	// once the upload lands — selecting before then would leave Run FitCheck disabled.
	useEffect(() => {
		const knownIds = dspIdsBeforeCreateRef.current;

		if (showCreateDSP || !knownIds) return;

		// The newest of them, not the first: a failed upload still leaves its DSP row behind, so a
		// retry inside the same modal session can produce more than one new id, and the one the
		// user actually uploaded is the last written.
		const newDSPs = dsps.filter((dsp) => !knownIds.includes(dsp.dsp_id) && dsp.source_resume?.storage_key);
		const created = newDSPs[newDSPs.length - 1];

		if (!created) return;

		dspIdsBeforeCreateRef.current = null;
		setSelectedDSPId(created.dsp_id);
	}, [dsps, showCreateDSP]);

	const createDSPModal = (
		<Modal
			isOpen={showCreateDSP}
			onClose={closeCreateDSP}
			title="Get noticed. Get Hired – Upload your resume!"
			size="xl"
		>
			<UploadResumeModal onClose={closeCreateDSP} />
		</Modal>
	);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center min-h-[400px]">
				<Loader />
			</div>
		);
	}

	// Empty state. No panel background: this already sits inside the FitCheck card, and a second
	// tinted surface read as a card within a card. The blue carries on the icon instead.
	if (dsps.length === 0) {
		return (
			<div className="w-full mx-auto">
				<div className="p-8 text-center flex flex-col items-center">
					<div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mb-6">
						<FileText className="w-10 h-10 text-blue-600" strokeWidth={1.5} />
					</div>
					<h2 className="text-xl font-semibold text-gray-900 mb-3">You haven't uploaded a resume yet.</h2>
					<p className="text-gray-700 mb-8 max-w-md">
						Your Resume helps us analyze your fit accurately. Upload a resume to unlock your personalized FitCheck
						results.
					</p>
					<Button onClick={openCreateDSP}>Upload Resume</Button>
				</div>
				{createDSPModal}
			</div>
		);
	}

	return (
		<div className="max-w-2xl">
			<div className="space-y-3 mb-6">
				{dsps.map((dsp) => (
					<div
						key={dsp.dsp_id}
						// Clicking the selected card clears it, which disables Run FitCheck again. Auto-select
						// after a create puts a choice in front of the user that they never made by hand, so
						// undoing it has to be possible.
						// A DSP with no source resume has nothing to compare against, so it cannot be picked.
						onClick={() =>
							dsp.source_resume?.storage_key &&
							setSelectedDSPId((current) => (current === dsp.dsp_id ? null : dsp.dsp_id))
						}
						className={`
              relative p-4 rounded-lg border-2 transition-all
              ${dsp.source_resume?.storage_key ? "cursor-pointer" : "opacity-60"}
              ${
								selectedDSPId === dsp.dsp_id
									? "border-blue-600 bg-blue-50"
									: "border-gray-200 bg-white hover:border-gray-300"
							}
            `}
					>
						<div className="flex items-start justify-between">
							<div className="flex-1">
								<h3 className="text-lg font-semibold text-gray-900 mb-1">{dsp.dsp_title}</h3>
								<p className="text-sm text-gray-600 mb-2">{dsp.dsp_description || "No description"}</p>
								<div className="flex items-center gap-1 text-sm text-gray-500">
									<FileText className="w-4 h-4" />
									<span>{dsp.source_resume ? `Resume: ${dsp.source_resume.file_name}` : "No resume attached"}</span>
								</div>
							</div>

							{selectedDSPId === dsp.dsp_id && (
								<div className="shrink-0 ml-4">
									<div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center motion-safe:animate-fc-pop">
										<svg
											className="w-4 h-4 text-white"
											fill="none"
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
											viewBox="0 0 24 24"
											stroke="currentColor"
										>
											<path d="M5 13l4 4L19 7" />
										</svg>
									</div>
								</div>
							)}
						</div>
					</div>
				))}
			</div>

			<button
				type="button"
				onClick={openCreateDSP}
				className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium mb-8"
			>
				<Plus className="w-5 h-5" />
				Create New DSP
			</button>

			<Button className="w-full" onClick={handleRunFitCheck} disabled={!selectedStorageKey}>
				Run FitCheck
			</Button>

			{createDSPModal}
		</div>
	);
}

export default SelectDSP;
